import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { CheckCircle2, AlertTriangle, ShieldAlert, Copy, Link2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import { formatCapacityError, isCapacityError } from "@/hooks/useAccountCapacity";


interface Campaign {
  id: string;
  name: string;
  workspace_id: string | null;
  previous_linkedin_provider_id: string | null;
  status: string;
}

interface AttachAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaign: Campaign;
  workspaceId: string;
  /** true when opened from super admin surface (used only for audit tag) */
  isAdmin?: boolean;
  onAttached?: () => void;
}

type MatchCase = "match" | "mismatch" | "unknown";

export function AttachAccountDialog({
  open,
  onOpenChange,
  campaign,
  workspaceId,
  isAdmin,
  onAttached,
}: AttachAccountDialogProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [confirmText, setConfirmText] = useState("");
  const [submitting, setSubmitting] = useState<"attach" | "duplicate" | null>(null);

  const { data: clients, isLoading } = useQuery({
    queryKey: ["attach-dialog-clients", workspaceId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("clients")
        .select("id, linkedin_name, email, profile_picture_url, linkedin_provider_id, status")
        .eq("workspace_id", workspaceId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: open && !!workspaceId,
  });

  const selectedClient = useMemo(
    () => clients?.find((c) => c.id === selectedClientId) || null,
    [clients, selectedClientId],
  );

  const matchCase: MatchCase | null = useMemo(() => {
    if (!selectedClient) return null;
    const prev = campaign.previous_linkedin_provider_id;
    const now = selectedClient.linkedin_provider_id;
    if (!prev) return "unknown";
    if (prev && now && prev === now) return "match";
    return "mismatch";
  }, [selectedClient, campaign.previous_linkedin_provider_id]);

  const requiresTypedConfirm = matchCase === "mismatch" || matchCase === "unknown";
  const confirmOk = !requiresTypedConfirm || confirmText.trim() === campaign.name.trim();

  const resetAndClose = () => {
    setSelectedClientId(null);
    setConfirmText("");
    setSubmitting(null);
    onOpenChange(false);
  };

  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["campaigns", workspaceId] }),
      queryClient.invalidateQueries({ queryKey: ["admin-workspace-campaigns", workspaceId] }),
      queryClient.invalidateQueries({ queryKey: ["campaign-stats", campaign.id] }),
    ]);
  };

  const handleAttach = async () => {
    if (!selectedClient || !confirmOk) return;
    setSubmitting("attach");
    try {
      // Guarded update — only succeeds if campaign is still unlinked.
      const { data, error } = await supabase
        .from("campaigns")
        .update({
          client_id: selectedClient.id,
          status: campaign.status === "completed" || campaign.status === "archived"
            ? campaign.status
            : "paused",
          paused_reason: isAdmin ? "attached_by_admin" : "attached_by_user",
          paused_at: new Date().toISOString(),
          account_deleted: false,
          previous_linkedin_provider_id: null,
          updated_at: new Date().toISOString(),
        })
        .eq("id", campaign.id)
        .is("client_id", null)
        .select("id")
        .maybeSingle();

      if (error) throw error;
      if (!data) {
        toast.error("This campaign was already attached to another account. Reloading…");
        await invalidate();
        resetAndClose();
        return;
      }

      // Best-effort audit — never block success.
      supabase.from("critical_operation_log").insert({
        operation: "campaign_attached_to_client",
        campaign_id: campaign.id,
        workspace_id: workspaceId,
        details: {
          new_client_id: selectedClient.id,
          new_provider_id: selectedClient.linkedin_provider_id,
          previous_provider_id: campaign.previous_linkedin_provider_id,
          match_case: matchCase,
          via: isAdmin ? "admin" : "user",
        },
      } as any).then(({ error: logErr }) => {
        if (logErr) console.warn("[AttachAccountDialog] audit log failed:", logErr.message);
      });

      toast.success("Account attached. Campaign remains paused — start it when you're ready.");
      await invalidate();
      onAttached?.();
      resetAndClose();
    } catch (err: any) {
      console.error(err);
      toast.error(isCapacityError(err) ? formatCapacityError(err) : err?.message || "Failed to attach account");
      setSubmitting(null);
    }
  };

  const handleDuplicate = async () => {
    if (!selectedClient) return;
    setSubmitting("duplicate");
    try {
      const { data, error } = await supabase.functions.invoke("duplicate-campaign", {
        body: {
          source_campaign_id: campaign.id,
          new_client_id: selectedClient.id,
          workspace_id: workspaceId,
        },
      });
      if (error) throw error;
      const newId = data?.new_campaign_id;
      if (!newId) throw new Error("Duplicate did not return a new campaign id");
      toast.success("Campaign duplicated. Add fresh targets, then start it.");
      await invalidate();
      onAttached?.();
      resetAndClose();
      navigate(`/campaigns/${newId}`);
    } catch (err: any) {
      console.error(err);
      toast.error(isCapacityError(err) ? formatCapacityError(err) : err?.message || "Failed to duplicate campaign");
      setSubmitting(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (!v ? resetAndClose() : onOpenChange(v))}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Attach LinkedIn account</DialogTitle>
          <DialogDescription>
            Pick a LinkedIn account from this workspace to attach to
            <span className="font-medium text-foreground"> {campaign.name}</span>.
            The campaign will stay paused — you decide when to start it.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">
            Available accounts
          </Label>
          <div className="max-h-64 overflow-y-auto rounded-md border divide-y">
            {isLoading ? (
              <div className="p-3 space-y-2">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            ) : !clients || clients.length === 0 ? (
              <div className="p-4 text-sm text-muted-foreground text-center">
                No LinkedIn accounts in this workspace. Connect one first.
              </div>
            ) : (
              clients.map((c) => {
                const isSel = c.id === selectedClientId;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => {
                      setSelectedClientId(c.id);
                      setConfirmText("");
                    }}
                    className={cn(
                      "w-full flex items-center gap-3 p-3 text-left hover:bg-muted/50 transition-colors",
                      isSel && "bg-primary/5",
                    )}
                  >
                    <Avatar className="h-9 w-9 shrink-0">
                      <AvatarImage src={c.profile_picture_url || undefined} />
                      <AvatarFallback>{c.linkedin_name?.[0] || "?"}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">
                        {c.linkedin_name?.trim() || c.email || "Unknown"}
                      </div>
                      <div className="text-[11px] text-muted-foreground truncate">
                        {c.linkedin_provider_id
                          ? `Provider: ${c.linkedin_provider_id.slice(0, 24)}…`
                          : "No provider ID"}
                      </div>
                    </div>
                    <Badge
                      variant="outline"
                      className={cn(
                        "text-[10px]",
                        c.status === "active"
                          ? "border-emerald-500/40 text-emerald-600"
                          : "border-amber-500/40 text-amber-600",
                      )}
                    >
                      {c.status || "unknown"}
                    </Badge>
                  </button>
                );
              })
            )}
          </div>

          {selectedClient && matchCase === "match" && (
            <Alert className="border-emerald-500/40 bg-emerald-500/5">
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
              <AlertTitle className="text-emerald-700 dark:text-emerald-400">
                Same LinkedIn account as before
              </AlertTitle>
              <AlertDescription className="text-emerald-700/90 dark:text-emerald-400/90">
                Safe to reattach — sequence and history stay consistent.
              </AlertDescription>
            </Alert>
          )}

          {selectedClient && matchCase === "mismatch" && (
            <Alert className="border-destructive/50 bg-destructive/5">
              <ShieldAlert className="h-4 w-4 text-destructive" />
              <AlertTitle className="text-destructive">
                Different LinkedIn account than original
              </AlertTitle>
              <AlertDescription className="text-destructive/90 space-y-2">
                <p>
                  This campaign was originally sent from another LinkedIn account.
                  Attaching a different account here can cause duplicate outreach
                  to the same prospects and mix send-history across accounts.
                </p>
                <p className="font-medium">Recommended: duplicate the campaign under this account and add fresh targets.</p>
              </AlertDescription>
            </Alert>
          )}

          {selectedClient && matchCase === "unknown" && (
            <Alert className="border-amber-500/50 bg-amber-500/5">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertTitle className="text-amber-700 dark:text-amber-400">
                Original account is unknown
              </AlertTitle>
              <AlertDescription className="text-amber-700/90 dark:text-amber-400/90 space-y-2">
                <p>
                  We can't verify which LinkedIn account originally ran this campaign,
                  so duplicating under this account is the safest option.
                </p>
                <p>
                  If you're certain this is the same person's account, you can attach
                  anyway — but existing targets will remain and previous send-history
                  cannot be validated.
                </p>
              </AlertDescription>
            </Alert>
          )}

          {selectedClient && requiresTypedConfirm && (
            <div className="space-y-1.5">
              <Label htmlFor="confirm-name" className="text-xs">
                Type <span className="font-mono">{campaign.name}</span> to enable "Attach anyway"
              </Label>
              <Input
                id="confirm-name"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={campaign.name}
                autoComplete="off"
              />
            </div>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="ghost"
            onClick={resetAndClose}
            disabled={!!submitting}
            className="sm:mr-auto"
          >
            Cancel
          </Button>

          {selectedClient && (matchCase === "mismatch" || matchCase === "unknown") && (
            <>
              <Button
                variant="outline"
                onClick={handleAttach}
                disabled={!confirmOk || !!submitting}
                title={!confirmOk ? "Type the campaign name to confirm" : undefined}
              >
                {submitting === "attach" ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Link2 className="mr-2 h-4 w-4" />
                )}
                Attach anyway
              </Button>
              <Button onClick={handleDuplicate} disabled={!!submitting}>
                {submitting === "duplicate" ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Copy className="mr-2 h-4 w-4" />
                )}
                Duplicate under this account
              </Button>
            </>
          )}

          {selectedClient && matchCase === "match" && (
            <Button onClick={handleAttach} disabled={!!submitting}>
              {submitting === "attach" ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Link2 className="mr-2 h-4 w-4" />
              )}
              Attach & keep paused
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
